import { Controller } from "@hotwired/stimulus"
import { i18n } from "../lib/i18n"

/**
 * Event Kiosk Controller
 *
 * Full-screen tablet-optimized check-in UI for event instances. Consumes
 * decode events from a nested `qr-scanner` Stimulus controller (which
 * owns the camera + html5-qrcode lifecycle and the shared UI states) and
 * POSTs each scanned token to the server. The server resolves the token
 * to a Neighbor (org-scoped via Neighbor::Identifier), runs
 * Events::CheckInNeighbor, and returns a turbo_stream that updates the
 * recent-check-ins list.
 *
 * Sensory feedback on scan (since staff often aren't looking at the
 * screen during rushes):
 *   - short beep (WebAudio, 880Hz, 120ms)
 *   - full-screen flash overlay (success-tinted on new, warning-tinted on
 *     duplicate, error-tinted on unknown) via the flashOverlay target
 *   - count bump animation when the running count changes (the count
 *     target is re-connected by Turbo after the stream replaces it)
 *
 * The server communicates the outcome via an `X-Event-Check-In-Status`
 * response header so the client can choose the correct flash tint
 * without parsing the turbo_stream HTML.
 */
export default class extends Controller<HTMLElement> {
  static values = {
    checkInUrl: String,
    csrfToken: String,
  }

  static targets = ["recentList", "count", "flashOverlay"]

  declare readonly checkInUrlValue: string
  declare readonly csrfTokenValue: string
  declare readonly hasRecentListTarget: boolean
  declare readonly recentListTarget: HTMLElement
  declare readonly hasCountTarget: boolean
  declare readonly countTarget: HTMLElement
  declare readonly hasFlashOverlayTarget: boolean
  declare readonly flashOverlayTarget: HTMLElement

  private beepAudio: HTMLAudioElement | null = null
  private lastCountValue: string | null = null

  connect() {
    this.prepareBeep()
  }

  // The count element is re-rendered on every scan via turbo_stream.replace.
  // Stimulus re-fires this callback when the new element attaches, letting
  // us compare before/after and animate only on real change.
  countTargetConnected(el: HTMLElement) {
    const current = (el.textContent || "").trim()
    if (this.lastCountValue !== null && this.lastCountValue !== current) {
      this.bumpCount(el)
    }
    this.lastCountValue = current
  }

  // Wired via `data-action="qr-scanner:decode->event-kiosk#onScan"` on the
  // element hosting the shared qr-scanner controller.
  async onScan(event: CustomEvent<{ token: string }>) {
    const token = event.detail?.token
    if (!token) return
    await this.submitToken(token)
  }

  async submitToken(token: string) {
    const formData = new FormData()
    formData.append("token", token)

    try {
      const response = await fetch(this.checkInUrlValue, {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": this.csrfTokenValue,
        },
        body: formData,
      })

      const status = response.headers.get("X-Event-Check-In-Status") ||
        (response.ok ? "new" : "error")

      if (response.ok) {
        // Audible feedback for both new and duplicate 200 responses. The
        // authoritative count comes from the server via turbo_stream, so
        // we do not increment it client-side (duplicate scans also 200).
        this.playBeep()
      }

      this.flash(status)

      const html = await response.text()
      if (html) {
        // @ts-expect-error - Turbo is provided by @hotwired/turbo-rails at runtime.
        const Turbo = (await import("@hotwired/turbo")).default ?? (await import("@hotwired/turbo"))
        Turbo.renderStreamMessage(html)
      }
    } catch {
      this.flash("error")
      this.renderErrorToast(i18n.t("event_kiosk.network_error"))
    }
  }

  // status: "new" | "duplicate" | "error"
  private flash(status: string) {
    if (!this.hasFlashOverlayTarget) return
    const el = this.flashOverlayTarget
    el.classList.remove("bg-success/30", "bg-warning/30", "bg-error/30", "opacity-0")
    const tint =
      status === "duplicate" ? "bg-warning/30" :
      status === "error" ? "bg-error/30" :
      "bg-success/30"
    el.classList.add(tint)
    el.classList.remove("opacity-0")
    // Fade out after ~220ms — long enough to register, short enough not to
    // block the next scan.
    window.setTimeout(() => {
      el.classList.add("opacity-0")
      el.classList.remove(tint)
    }, 220)
  }

  private bumpCount(el: HTMLElement) {
    el.classList.remove("scale-100")
    el.classList.add(
      "inline-block",
      "transition-transform",
      "duration-200",
      "ease-out",
      "scale-125",
    )
    window.setTimeout(() => {
      el.classList.remove("scale-125")
      el.classList.add("scale-100")
    }, 220)
  }

  private renderErrorToast(message: string) {
    const container = document.getElementById("toast_container")
    if (!container) return
    const el = document.createElement("div")
    el.className = "alert alert-error"
    el.textContent = message
    container.prepend(el)
    setTimeout(() => el.remove(), 3000)
  }

  private prepareBeep() {
    // Lazy WebAudio beep — created on first scan to satisfy browser
    // autoplay policies that require a user gesture before AudioContext
    // can emit sound.
    this.beepAudio = null
  }

  private playBeep() {
    try {
      // Using WebAudio rather than an <audio> element to avoid bundling
      // an asset for a simple 440Hz blip.
      const AudioContextClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!AudioContextClass) return
      const ctx = new AudioContextClass()
      const oscillator = ctx.createOscillator()
      const gain = ctx.createGain()
      oscillator.type = "sine"
      oscillator.frequency.value = 880
      gain.gain.value = 0.12
      oscillator.connect(gain)
      gain.connect(ctx.destination)
      oscillator.start()
      oscillator.stop(ctx.currentTime + 0.12)
      oscillator.onended = () => {
        void ctx.close()
      }
    } catch {
      /* audio is best-effort */
    }
  }
}
